import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject, Input,
  OnInit, ViewChild } from '@angular/core';

import { IQuestionEventsService, QUESTION_EVENTS_SERVICE_TOKEN } from '../../../../services/IQuestionEvents';
import { parseFrameString } from '../../../../utils/utils';
import { InputColor } from '../../../enums/input-color.enum';
import { IScorable, ScorableData } from '../kh-manip-scorable/IScorable';

export enum RegionOriginMode {
  TOP_LEFT,
  BOT_LEFT
}

@Component({
  selector: 'kh-manipulative-num-input',
  templateUrl: 'kh-manip-scorable-int-input.component.html',
  styleUrls: ['kh-manip-scorable-int-input.component.less'],
  standalone: false
})
export class KhManipScorableIntInput implements IScorable, OnInit, AfterViewInit {
  public onChangeCallback: undefined | ((data: ScorableData) => void); // callback
  // keep this name "value" when it should really be displayedValue due to existing questions being made with it called "value"
  @Input() public value = '';
  @Input() public name = '';
  @Input() public key = '';
  @Input('ignore-in-score') public ignoreInScore = false;
  @Input() public frame = '0 0 100 100';
  @ViewChild('input') public inputElement!: ElementRef<HTMLInputElement>;
  @Input('font-family') protected fontFamily = 'arial';
  @Input('max-digits') public maxLength = 40;
  @Input('text-size') public textSize = '80%';
  @Input('border-stroke-size') public borderStrokeSize = 3;
  @Input('background-color') public backgroundColor = 'transparent';
  @Input('text-alignment') public textAlignment = 'center';
  @Input('text-style') public textStyle = 'normal';
  @Input() public shape?: string = 'rectagle';
  @Input('broken-keys') public brokenKeys: number[] = [];
  @Input('stick-to') public stickTo = '';
  @Input('border-radius') public borderRadius = '';
  @ViewChild('keyboard') public keyboard!: ElementRef<HTMLElement>;

  public borderColor = InputColor.DEFAULT_COLOR;
  public inputColor = InputColor;
  public fontSize = 16;
  public fontWeight = 'normal';
  public fontStyle = 'normal';

  public box = {
    left: 0,
    top: 0,
    width: 0,
    height: 0
  };

  public showInputKeypad = false;
  public keyboardPositionSet = false;
  public keyboardLeft = 0;
  public keyboardTop = 0;

  private x = 0;
  private y = 0;
  private origin = RegionOriginMode.BOT_LEFT;
  private parentOffset = 0;
  private _hasOutline = 'true';

  // ---- Auto-fit additions ----
  private defaultFontSize = 16; // cap for growth (from text-size)
  private minFontSize = 8; // floor to keep text legible
  private measureCtx: CanvasRenderingContext2D | null = null; // offscreen measure ctx
  // ----------------------------

  @Input('hasoutline')
  public set hasOutlineAlias(value: string) {
    this._hasOutline = value;
  }

  @Input('has-outline')
  public set hasOutlineDashed(value: string) {
    this._hasOutline = value;
  }

  public get hasOutline(): string {
    return this._hasOutline;
  }

  public get boxShadow(): string {
    const color = (this.value != null && this.value !== '') || this.showInputKeypad
      ? this.inputColor.ACTIVE_COLOR
      : this.inputColor.DEFAULT_COLOR;
    const half = this.borderStrokeSize / 2;
    return `inset 0 0 0 ${half}px ${color}, 0 0 0 ${half}px ${color}`;
  }

  public ngOnInit() {
    if (!this.borderRadius) {
      this.borderRadius = this.shape?.toLocaleLowerCase() === 'oval' ? '50%' : '5px';
    }
    else {
      this.borderRadius = this.borderRadius.endsWith('%') ? this.borderRadius : `${parseFloat(this.borderRadius)}px`;
    }

    if (this.hasOutline === 'false') {
      this.borderStrokeSize = 0;
    }

    const frameData = parseFrameString(this.frame);

    this.box = {
      left: frameData.x,
      top: frameData.y,
      width: frameData.width,
      height: frameData.height
    };

    this.fontSize = this.textSize.endsWith('%')
      ? (Number(this.textSize.replace('%', '')) / 100) * frameData.height
      : Number(this.textSize);

    this.defaultFontSize = this.fontSize; // remember initial
    this.maxLength = parseInt(this.maxLength.toString());

    if (this.textStyle === 'bold') {
      this.fontWeight = 'bold';
    }
    else {
      this.fontStyle = this.textStyle;
    }

    // prepare offscreen canvas for measurement
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) { this.measureCtx = ctx; }

    // initial fit in case value is pre-filled
    this.updateFontSizeToFit();
  }

  public ngAfterViewInit(): void {
    const frameData = parseFrameString(this.frame);
    if (this.stickTo) {
      this.keyboardLeft = Number(this.stickTo.replace(/[']/g, '').split(',')[0]);
      this.keyboardTop = Number(this.stickTo.replace(/[']/g, '').split(',')[1]);
    }
    else {
      this.keyboardTop = frameData.y + frameData.height;
      this.keyboardLeft =
        frameData.x + (this.inputElement.nativeElement.offsetWidth / 2) - 200 >= 0
          ? frameData.x + (this.inputElement.nativeElement.offsetWidth / 2) - 200
          : 0;
    }

    // Prevents the input from popping up
    this.inputElement.nativeElement.addEventListener('focus', (e: FocusEvent) => {
      e.preventDefault();
    });

    // run fit once view is ready (actual width known)
    this.updateFontSizeToFit();
  }

  public constructor(
    private readonly cdr: ChangeDetectorRef,
    @Inject(QUESTION_EVENTS_SERVICE_TOKEN) protected readonly questionEventsService: IQuestionEventsService
  ) {
    this.questionEventsService.hideInputOnlyForManip.next(true);
  }

  public onChangeValue(val: string): void {
    this.value = val.replace(/^"(.+)"$/, '$1');

    if (val.length > this.maxLength) {
      this.value = '';
      this.cdr.detectChanges();
      const substring = val.substring(0, this.maxLength);
      this.value = parseInt(substring).toString();
    }

    // Auto-fit whenever content changes
    this.updateFontSizeToFit();

    if (this.onChangeCallback) {
      this.onChangeCallback(this.getScorableData());
    }

    this.inputElement.nativeElement.value = this.value;
  }

  public getScorableData(): ScorableData {
    const data: ScorableData = { answer: {}, state: {} };

    // get value as a pure numeric string
    const numericString = (this.value.trim()
      .replace(/^(-)?.*/, (_, neg) => { // capture leading minus if any
        const digits = this.value.replace(/\D/g, ''); // remove all non-digits
        return (neg ? '-' : '') + digits;
      })
    );

    if (this.value) {
      if (!this.ignoreInScore) {
        data.answer[this.key] = numericString;
      }

      data.state[this.key] = numericString;
    }

    return data;
  }

  public setScorableData(data: ScorableData): void {
    const keys = Object.keys(data.answer);

    for (let i = 0; i < keys.length; i++) {
      if (keys[i] === this.key) {
        this.value = data.answer[keys[i]];
        this.inputElement.nativeElement.value = this.value;
        // ensure fitted when restored
        this.updateFontSizeToFit();
        return;
      }
    }
  }

  public showInput() {
    this.showInputKeypad = true;
  }

  private countNumericLength(val: string): number {
    const numericString = val.replace(/[^0-9]/g, '');
    return numericString.length;
  }

  public valueChanged(val: string): void {
    let text;

    if (!val) {
      text = '';
    }
    else if (val == '00' || val == '0') {
      text = '0';
    }
    else {
      // removes all leading zeros
      text = val.replace(/^[0.]+/, '').toString();
    }

    this.value = text;

    if (this.countNumericLength(this.value) > this.maxLength) {
      this.value = '';
      this.cdr.detectChanges();
      const substring = val.substring(0, this.maxLength);
      this.value = parseInt(substring).toString();
    }

    // Auto-fit on keypad-driven changes too
    this.updateFontSizeToFit();

    if (this.onChangeCallback) {
      this.onChangeCallback(this.getScorableData());
    }

    this.inputElement.nativeElement.value = this.value;
  }

  public KeyboardStyles(): Record<string, any> {
    return {
      left: `${this.keyboardLeft}px`,
      top: `${this.keyboardTop}px`
    };
  }

  public onClose = () => {
    this.showInputKeypad = false;
    this.keyboardPositionSet = false;
  };

  public setOriginMode(origin: RegionOriginMode): void {
    this.origin = origin;
  }

  public setOffset(offset: number): void {
    this.parentOffset = offset;
  }

  // Re-fit on window resize (e.g., if container/frame changes)
  @HostListener('window:resize')
  private onWindowResize(): void {
    this.updateFontSizeToFit();
  }

  /** Measure current value and adjust fontSize so all digits fit within the input region width. */
  private updateFontSizeToFit(): void {
    // guard
    if (!this.measureCtx) { return; }

    // available width: prefer actual element width, fallback to frame width
    const elWidth = this.inputElement?.nativeElement?.clientWidth ?? this.box.width ?? 0;

    // leave a tiny 2px safety so we don’t clip on subpixel renders
    const availableWidth = Math.max(0, elWidth - 2);

    const textToMeasure = this.value?.length ? this.value : '0';

    // start from current but cap at default and floor at min
    let size = Math.min(this.fontSize || this.defaultFontSize, this.defaultFontSize);
    size = Math.max(size, this.minFontSize);

    const setCtxFont = (s: number) => {
      // CSS font shorthand: style weight size family
      if (this.measureCtx) {
        this.measureCtx.font = `${this.fontStyle} ${this.fontWeight} ${String(s)}px ${this.fontFamily}`;
      }
    };

    setCtxFont(size);
    let measured = this.measureCtx.measureText(textToMeasure).width;

    if (availableWidth <= 0) { return; }

    // If overflow, shrink proportionally then fine-tune
    if (measured > availableWidth) {
      const ratio = availableWidth / measured;
      size = Math.max(this.minFontSize, Math.floor(size * ratio));
      setCtxFont(size);
      measured = this.measureCtx.measureText(textToMeasure).width;

      while (measured > availableWidth && size > this.minFontSize) {
        size -= 1;
        setCtxFont(size);
        measured = this.measureCtx.measureText(textToMeasure).width;
      }
    }
    else if (measured < availableWidth && size < this.defaultFontSize) {
      // Grow back (gentle) if there’s room and we’re below default
      const ratio = availableWidth / Math.max(1, measured);
      const target = Math.min(this.defaultFontSize, Math.floor(size * Math.min(ratio, 1.2)));
      if (target > size) {
        size = target;
        setCtxFont(size);
        measured = this.measureCtx.measureText(textToMeasure).width;

        // if small overshoot due to rounding, step down
        while (measured > availableWidth && size > this.minFontSize) {
          size -= 1;
          setCtxFont(size);
          measured = this.measureCtx.measureText(textToMeasure).width;
        }
      }
    }

    if (size !== this.fontSize) {
      this.fontSize = size;
      this.cdr.markForCheck?.();
    }
  }
}
